import { Injectable, OnDestroy } from '@angular/core';
import { DocumentService } from 'src/app/services/document/document.service';
import { CountryService } from 'src/app/services/country/country.service';
import { DocumentSection } from 'src/app/models/document-section';
import { SectionReference } from 'src/app/models/section-reference';
import { Observable, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SectionTooltipService implements OnDestroy {
  visible = false;
  loading = false;
  title = '';
  content = '';
  /** The section's references as free-text HTML */
  references = '';
  referenceList: SectionReference[] = [];
  referencesCheckedOn: string | null = null;
  x = 0;
  y = 0;
  tooltipHovered = false;

  private destroy$ = new Subject<void>();
  private hideTimeout: any = null;

  constructor(
    private documentService: DocumentService,
    private countryService: CountryService,
  ) {}

  show(sectionId: number, x: number, y: number) {
    this.cancelHide();
    this.x = x;
    this.y = y;
    this.visible = true;
    this.loading = true;
    this.title = '';
    this.content = '';
    this.clearReferences();

    this.documentService.getDocumentSectionTemplate(sectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (section: DocumentSection) => this.displaySection(section),
        error: () => this.showError(),
      });
  }

  showFromUrl(url: string, x: number, y: number) {
    this.cancelHide();
    this.x = x;
    this.y = y;
    this.visible = true;
    this.loading = true;
    this.title = '';
    this.content = '';
    this.clearReferences();

    const sectionId = this.extractSectionId(url);
    const countrySlug = this.extractCountrySlug(url);

    if (!sectionId || !countrySlug) {
      this.showError();
      return;
    }

    const section$: Observable<DocumentSection> = this.isSnapshotUrl(url)
      ? this.getSnapshotSection(countrySlug, sectionId)
      : this.documentService.getDocumentSectionTemplate(sectionId);

    section$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (section: DocumentSection) => this.displaySection(section),
      error: () => this.showError(),
    });
  }

  /** Show a fixed piece of text in the panel, with no section to fetch. */
  showText(text: string, x: number, y: number) {
    this.cancelHide();
    this.x = x;
    this.y = y;
    this.visible = true;
    this.loading = false;
    this.title = '';
    this.content = text;
    this.clearReferences();
  }

  hide() {
    this.visible = false;
    this.loading = false;
    this.tooltipHovered = false;
  }

  scheduleHide() {
    this.hideTimeout = setTimeout(() => {
      if (!this.tooltipHovered) {
        this.hide();
      }
    }, 150);
  }

  cancelHide() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  private displaySection(section: DocumentSection | undefined) {
    this.loading = false;
    if (section) {
      this.title = section.name;
      this.content = section.content || '';
      this.references = section.references || '';
      this.referenceList = section.reference_list || [];
      this.referencesCheckedOn = section.references_checked_on || null;
    } else {
      this.content = 'Preview not available';
    }
  }

  private showError() {
    this.loading = false;
    this.content = 'Preview not available';
  }

  private getSnapshotSection(countrySlug: string, sectionId: number): Observable<DocumentSection> {
    return this.countryService.getCountry(countrySlug).pipe(
      switchMap(country => this.documentService.getCountrySnapshotSections(country.iso2)),
      filter(sections => sections && sections.length > 0),
      take(1),
      map(sections => sections.find(s => s.id === sectionId)),
    );
  }

  private clearReferences() {
    this.references = '';
    this.referenceList = [];
    this.referencesCheckedOn = null;
  }

  private extractSectionId(url: string): number | null {
    try {
      const urlObj = new URL(url, window.location.origin);
      const idParam = urlObj.searchParams.get('id');
      if (idParam) {
        const id = parseInt(idParam, 10);
        if (!isNaN(id)) return id;
      }
      return null;
    } catch {
      return null;
    }
  }

  private extractCountrySlug(url: string): string | null {
    try {
      const urlObj = new URL(url, window.location.origin);
      const match = urlObj.pathname.match(/\/country\/([^\/]+)\//);
      return match ? match[1] : null;
    } catch {
      return null;
    }
  }

  private isSnapshotUrl(url: string): boolean {
    try {
      const urlObj = new URL(url, window.location.origin);
      return urlObj.pathname.includes('/snapshot');
    } catch {
      return false;
    }
  }

  ngOnDestroy() {
    this.cancelHide();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
